'use client'

import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  OAuthProvider,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
} from 'firebase/auth'
import Cookies from 'js-cookie'
import { ENTRY_COOKIE, parseEntryCookie } from '../lib/entry-source'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaApple, FaCheck, FaGoogle } from 'react-icons/fa'
import { MdEmail } from 'react-icons/md'
import ClipLoader from 'react-spinners/ClipLoader'
import { auth } from '../config/firebase'
import { emailexceptions, realEmails } from '../helpers/config'
import Modal from './Modal'
import { useAuth } from '../contexts/AuthContext'
import { CreateContext } from '../contexts/CreateContext'
import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from '../helpers/safeStorage'
import {
  ACCOUNT_ATTRIBUTION_KEYS,
  accountAttribution,
  completeAccount,
  settleAccountCompletion,
} from '../lib/account-completion'
import {
  affiliateGroup,
  carryAttribution,
  readReferralCookie,
} from '../lib/hydrate-referral'
import { REFERRAL_COOKIE } from '../lib/referral-redirects'
import {
  getLandingExperimentToken,
  getLandingClickSnapshot,
  persistAccountLandingToken,
} from '../lib/landing-experiment-client'
import { LANDING_TOKEN_PARAM } from '../lib/landing-experiment'
import { takeReturnTo } from '../lib/return-to'

const authErrorMessage = (error) =>
  (error?.message || 'Could not sign you in. Please try again.')
    .replace('Firebase:', '')
    .trim()

const SignUpComponent = ({ message }) => {
  const { t } = useTranslation()
  const {
    user,
    authResolved,
    accountDocumentStatus,
    userDataLoaded,
    retryAccountData,
    beginAccountCompletion,
    finishAccountCompletion,
    cancelAccountCompletion,
  } = useAuth()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loginMode, setLoginMode] = useState(false)
  const [passwordLogin, setPasswordLogin] = useState(false)
  const [passwordEmail, setPasswordEmail] = useState('')
  const [password, setPassword] = useState('')
  const [termsModalOpen, setTermsModalOpen] = useState(false)
  const [confirmEmailModalOpen, setConfirmEmailModalOpen] = useState(false)
  const [confirmationEmail, setConfirmationEmail] = useState('')
  const [accountRetry, setAccountRetry] = useState(false)
  const handledEmailLink = useRef(false)
  const resumedSession = useRef(false)
  const authInProgress = useRef(false)
  const completion = useRef(null)
  const attribution = useRef(null)
  const formActive = useRef(true)
  const activeAttempt = useRef(null)

  const { setImageSrc, setVideoSrc } = useContext(CreateContext)
  const router = useRouter()

  useEffect(() => {
    formActive.current = true
    return () => {
      formActive.current = false
      cancelAccountCompletion(activeAttempt.current)
    }
  }, [cancelAccountCompletion])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setLoginMode(params.get('mode') === 'login')
    setPasswordLogin(params.get('login') === '1')
  }, [])

  const finishSignIn = useCallback(
    async (firebaseUser, attempt) => {
      if (completion.current) return completion.current
      if (!attribution.current) {
        const landingToken = getLandingExperimentToken()
        attribution.current = accountAttribution(
          safeGetItem,
          landingToken,
          getLandingClickSnapshot(landingToken),
          parseEntryCookie(Cookies.get(ENTRY_COOKIE)),
          readReferralCookie(),
        )
      }
      completion.current = settleAccountCompletion({
        uid: firebaseUser.uid,
        getCurrentUid: () => auth.currentUser?.uid,
        isFormActive: () =>
          formActive.current && activeAttempt.current === attempt,
        request: async () => {
          const idToken = await firebaseUser.getIdToken()
          return completeAccount({
            endpoint: `${process.env.NEXT_PUBLIC_PROMPTCHAN_PAYMENT}/api/create_account`,
            idToken,
            attribution: attribution.current,
          })
        },
        // An abandoned form may refresh the same account's durable data, but may
        // not redirect, clear drafts/referrals, or overwrite a newer sign-in.
        applyAccount: (result) =>
          finishAccountCompletion(result, attempt, firebaseUser.uid),
        onActiveSuccess: (result) => {
          persistAccountLandingToken(
            attribution.current.landing_experiment_token,
            result,
          )
          for (const key of ACCOUNT_ATTRIBUTION_KEYS) safeRemoveItem(key)
          Cookies.remove('aiffiliateUid')
          Cookies.remove('aiffiliateTracker')
          Cookies.remove('emailForSignIn')
          // The referral cookie too: it is no longer deleted when it is read
          // (lib/hydrate-referral.js), and would store the same click again.
          Cookies.remove(REFERRAL_COOKIE, { path: '/' })
          setImageSrc([])
          setVideoSrc([])
          setAccountRetry(false)
          resumedSession.current = true
          router.replace(takeReturnTo() || '/explore')
        },
      })
      try {
        return await completion.current
      } finally {
        completion.current = null
        if (auth.currentUser?.uid !== firebaseUser.uid)
          cancelAccountCompletion(attempt)
      }
    },
    [
      cancelAccountCompletion,
      finishAccountCompletion,
      router,
      setImageSrc,
      setVideoSrc,
    ],
  )

  // Email, Google, Apple, and interrupted signups share durable completion.
  // An ordinary restored session does not repeat account creation.
  const runSignIn = useCallback(
    async (authenticate) => {
      if (authInProgress.current) return
      authInProgress.current = true
      const attempt = beginAccountCompletion()
      activeAttempt.current = attempt
      setLoading(true)
      setError('')
      let authenticated = false
      try {
        const firebaseUser = await authenticate()
        authenticated = true
        await finishSignIn(firebaseUser, attempt)
      } catch (err) {
        if (formActive.current) {
          setError(authErrorMessage(err))
          setAccountRetry(authenticated)
        }
        if (!authenticated || !formActive.current)
          cancelAccountCompletion(attempt)
      } finally {
        authInProgress.current = false
        if (formActive.current) setLoading(false)
      }
    },
    [beginAccountCompletion, cancelAccountCompletion, finishSignIn],
  )

  useEffect(() => {
    if (!authResolved || typeof window === 'undefined') return
    if (!isSignInWithEmailLink(auth, window.location.href)) return
    if (handledEmailLink.current) return
    handledEmailLink.current = true
    const storedEmail =
      safeGetItem('emailForSignIn') || Cookies.get('emailForSignIn') || ''
    if (!storedEmail) {
      setConfirmEmailModalOpen(true)
      return
    }
    runSignIn(async () => {
      // A reload after an account-write failure must not consume the same
      // one-time email link again. The established Firebase session is enough.
      if (
        auth.currentUser?.email?.toLowerCase() === storedEmail.toLowerCase()
      ) {
        return auth.currentUser
      }
      const credential = await signInWithEmailLink(
        auth,
        storedEmail,
        window.location.href,
      )
      return credential.user
    })
  }, [authResolved, runSignIn])

  useEffect(() => {
    if (
      !authResolved ||
      !user ||
      resumedSession.current ||
      authInProgress.current
    )
      return
    if (isSignInWithEmailLink(auth, window.location.href)) return
    if (accountDocumentStatus === 'ready') {
      resumedSession.current = true
      router.replace(takeReturnTo() || '/explore')
    } else if (accountDocumentStatus === 'missing') {
      resumedSession.current = true
      runSignIn(async () => user)
    }
  }, [authResolved, user, accountDocumentStatus, router, runSignIn])

  const handleConfirmEmail = async () => {
    if (!confirmationEmail.trim()) {
      setError('Email is required to complete sign-in')
      return
    }
    setConfirmEmailModalOpen(false)
    safeSetItem('emailForSignIn', confirmationEmail.trim())
    Cookies.set('emailForSignIn', confirmationEmail.trim(), { expires: 0.1 })
    await runSignIn(async () => {
      if (
        auth.currentUser?.email?.toLowerCase() ===
        confirmationEmail.trim().toLowerCase()
      ) {
        return auth.currentUser
      }
      const credential = await signInWithEmailLink(
        auth,
        confirmationEmail.trim(),
        window.location.href,
      )
      return credential.user
    })
  }

  const handleSignUpWithEmail = async () => {
    if (!email.trim()) {
      setError(t('enterEmailAddress'))
      return
    }
    if (email.includes('+') && !emailexceptions.includes(email)) {
      setError(t('notSupportedEmail'))
      return
    }
    if (!realEmails.includes(email.split('@')[1])) {
      setError(t('notSupportedEmail'))
      return
    }
    setLoading(true)
    try {
      // Use a clean continue URL: never forward an existing Firebase action code,
      // email, or credential. The signed experiment token survives another device,
      // and so does the affiliate who brought them (stored there only when that
      // browser has none of its own).
      const continueUrl = carryAttribution(
        new URL('/signup', window.location.origin),
        affiliateGroup(safeGetItem, readReferralCookie()),
      )
      const experimentToken = getLandingExperimentToken()
      if (experimentToken) {
        continueUrl.searchParams.set(LANDING_TOKEN_PARAM, experimentToken)
      }
      await sendSignInLinkToEmail(auth, email, {
        url: continueUrl.toString(),
        handleCodeInApp: true,
      })
      safeSetItem('emailForSignIn', email)
      Cookies.set('emailForSignIn', email, { expires: 0.1 })
      setEmailSent(true)
      setError('')
    } catch (err) {
      setError(authErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  // Password sign-in is LOGIN ONLY and hidden behind /signup?login=1. It exists
  // so an account can be handed to someone who cannot use a magic link or a
  // Google/Apple identity of their own: ad-network and partner reviewers
  // (ExoClick, Oct 2026), as the affiliate app does for marketplace reviewers.
  // Passwords are minted by hand (backend docs: runbooks/reviewer-accounts.md);
  // there is deliberately no createUserWithEmailAndPassword here, so a wrong
  // email fails instead of quietly creating an account.
  const handlePasswordLogin = () => {
    if (!passwordEmail.trim() || !password) {
      setError('Enter your email and password')
      return
    }
    runSignIn(async () => {
      const credential = await signInWithEmailAndPassword(
        auth,
        passwordEmail.trim(),
        password,
      )
      return credential.user
    })
  }

  const handleSignUpWithGoogle = () =>
    runSignIn(async () => {
      const credential = await signInWithPopup(auth, new GoogleAuthProvider())
      return credential.user
    })

  const handleSignUpWithApple = () =>
    runSignIn(async () => {
      const credential = await signInWithPopup(
        auth,
        new OAuthProvider('apple.com'),
      )
      return credential.user
    })

  const needsAccountDataRetry =
    authResolved &&
    user &&
    userDataLoaded &&
    accountDocumentStatus === 'unknown' &&
    !handledEmailLink.current

  const handleRetryAccountData = async () => {
    setLoading(true)
    setError('')
    try {
      await retryAccountData()
    } catch (err) {
      if (formActive.current) setError(authErrorMessage(err))
    } finally {
      if (formActive.current) setLoading(false)
    }
  }

  return (
    <>
      {/* Modal for terms of service */}
      <Modal
        isOpen={termsModalOpen}
        closeModal={() => setTermsModalOpen(false)}
        title={t('termsreq')}
        content={t('toproceed')}
        confirmation="Done"
        onConfirm={() => setTermsModalOpen(false)}
      />

      {/* NEW: Modal to ask user for their email when completing email-link sign-in */}
      <Modal
        isOpen={confirmEmailModalOpen}
        closeModal={() => setConfirmEmailModalOpen(false)}
        title="Please confirm your email"
        content={
          <input
            type="email"
            placeholder={t('enterEmailAddress')}
            className="
              px-10 py-2
              text-white bg-black
              border-2 border-gray-400
              rounded-xl
              focus:border-gray-600 focus:outline-none
              my-6
              mx-3
              w-full
            "
            value={confirmationEmail}
            onChange={(e) => setConfirmationEmail(e.target.value)}
          />
        }
        confirmation="Confirm"
        isConfirmDisabled={!confirmationEmail}
        onConfirm={handleConfirmEmail}
      />

      <section className="container mx-auto px-4 pb-8 mt-5">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12 mx-auto max-w-md lg:max-w-4xl bg-[var(--middle-bg-color)] rounded-lg p-6 sm:p-8 shadow-2xl">
          <div className="w-full max-w-sm lg:shrink-0">
            <div className="mt-5 rounded-xl border border-white/10 bg-white/5 p-4 text-left lg:block hidden">
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-300">
                {t('whatYoullInstantlyUnlock')}
              </p>

              <img
                loading="lazy"
                src="/assets/landing_pose_safe2.webp"
                className="rounded-2xl w-full p-2 lg:block hidden"
                alt={t('signUpImageAlt')}
              />

              <ul className="mt-3 space-y-2 text-sm text-white">
                {[
                  'aiGirlfriendChat',
                  'createAiImagesNoFilter',
                  'starterCreditsIncluded',
                  'noCreditCardRequired',
                ].map((key) => (
                  <li key={key} className="flex items-center gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--main-bg-color)] text-white">
                      <FaCheck size={10} />
                    </span>
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="w-full max-w-md">
            {message && (
              <h1 className="mb-6 text-2xl tracking-tight text-white text-center font-semibold">
                {message ? message : t('continueToPromptchan')}
              </h1>
            )}
            {emailSent && (
              <p className="text-green-500 text-sm mb-2 text-center">
                {t('emailSentMessage')}
              </p>
            )}
            {error && (
              <p className="text-red-500 text-sm mb-2 text-center">{error}</p>
            )}
            {needsAccountDataRetry && (
              <div className="mb-4 text-center" role="status">
                <p className="text-sm text-gray-300 mb-3">
                  You're signed in, but your account details couldn't be loaded.
                </p>
                <button
                  type="button"
                  disabled={loading}
                  className="w-full rounded-xl border border-white/30 px-4 py-3 text-white disabled:opacity-50"
                  onClick={handleRetryAccountData}
                >
                  {loading
                    ? 'Loading your account…'
                    : 'Retry loading your account'}
                </button>
              </div>
            )}
            {accountRetry && (
              <button
                type="button"
                disabled={loading}
                className="w-full rounded-xl border border-white/30 px-4 py-3 mb-4 text-white disabled:opacity-50"
                onClick={() =>
                  runSignIn(async () => {
                    if (!auth.currentUser)
                      throw new Error('Please sign in again.')
                    return auth.currentUser
                  })
                }
              >
                {loading ? 'Finishing sign-in…' : 'Retry sign-in'}
              </button>
            )}

            {passwordLogin && (
              <form
                className="mb-6"
                onSubmit={(e) => {
                  e.preventDefault()
                  handlePasswordLogin()
                }}
              >
                <input
                  type="email"
                  autoComplete="username"
                  placeholder={t('enterEmailAddress')}
                  className="w-full px-3 py-2 mb-3 text-white bg-transparent border-2 border-gray-500 rounded-xl focus:border-gray-300 focus:outline-none"
                  value={passwordEmail}
                  onChange={(e) => setPasswordEmail(e.target.value)}
                />
                <input
                  type="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  className="w-full px-3 py-2 mb-3 text-white bg-transparent border-2 border-gray-500 rounded-xl focus:border-gray-300 focus:outline-none"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 px-4 flex items-center justify-center font-semibold bg-gray-300 text-black rounded-xl focus:outline-none"
                  data-track="signup.password"
                >
                  {loading ? (
                    <ClipLoader color="#333" loading={loading} size={18} />
                  ) : (
                    'Sign in with password'
                  )}
                </button>
              </form>
            )}

            <button
              disabled={loading}
              className={`
                w-full
                h-12
                px-4
                flex items-center justify-center space-x-2
                font-semibold
                transition-transform duration-200 hover:scale-[1.02]
                bg-[var(--main-bg-color)] text-white
                rounded-xl focus:outline-none
                mt-2
              `}
              data-track="signup.google"
              onClick={handleSignUpWithGoogle}
            >
              {loading ? (
                <ClipLoader color="#EEE" loading={loading} size={18} />
              ) : (
                <>
                  <FaGoogle />
                  <span>{t('continueWithGoogle')}</span>
                </>
              )}
            </button>

            <button
              disabled={loading}
              className={`
                w-full
                h-12
                px-4
                flex items-center justify-center space-x-2
                font-semibold
                transition-transform duration-200 hover:scale-[1.02]
                bg-[var(--main-bg-color)] text-white
                rounded-xl focus:outline-none
                mt-4
              `}
              data-track="signup.apple"
              onClick={handleSignUpWithApple}
            >
              {loading ? (
                <ClipLoader color="#EEE" loading={loading} size={18} />
              ) : (
                <>
                  <FaApple />
                  <span>{t('continueWithApple')}</span>
                </>
              )}
            </button>

            <p className="text-center text-gray-400 my-6">{t('or')}</p>

            <form onSubmit={(e) => e.preventDefault()}>
              <div className="mb-3">
                <input
                  type="email"
                  placeholder={t('enterEmailAddress')}
                  className="
                    w-full
                    px-3 py-2
                    text-white bg-transparent
                    border-2 border-gray-500
                    rounded-xl
                    focus:border-gray-300 focus:outline-none
                  "
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button
                // Only `loading` disables it. The muted styling below still
                // signals "not ready", but the press is always acknowledged —
                // see handleSignUpWithEmail.
                disabled={loading}
                className={`
                  w-full
                  h-12
                  px-4
                  flex items-center justify-center space-x-2
                  font-semibold
                  transition-transform duration-200 hover:scale-[1.02]
                  ${
                    email && !loading
                      ? 'bg-gray-300 text-black'
                      : 'bg-[var(--secondary-bg-color)] text-gray-300'
                  }
                  rounded-xl focus:outline-none
                  mb-4
                `}
                data-track="signup.email"
                onClick={handleSignUpWithEmail}
              >
                {loading ? (
                  <ClipLoader color="#EEE" loading={loading} size={18} />
                ) : loginMode ? (
                  <>
                    <MdEmail />
                    <span>{t('loginWithEmail')}</span>
                  </>
                ) : (
                  <>
                    <MdEmail />
                    <span>{t('continueWithEmail')}</span>
                  </>
                )}
              </button>
            </form>

            <button
              className="underline text-center text-gray-400 w-full text-sm"
              onClick={() => setLoginMode(!loginMode)}
            >
              {loginMode
                ? `Don't have an account?`
                : 'Already have an account?'}
            </button>

            <p className="text-center text-gray-400 mt-8 text-sm">
              {<>{t('registrationConfirmation')}</>} {t('byUsingThisWebsite')}{' '}
              <Link
                href="https://promptchan.com/termsofservice"
                prefetch={false}
                className="text-white underline"
              >
                {t('termsOfService')}
              </Link>{' '}
              {t('and')}{' '}
              <Link
                href="/privacypolicy"
                prefetch={false}
                className="text-white underline"
              >
                {t('privacyPolicy')}
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </>
  )
}

export default SignUpComponent
